import { UPLOAD_PURPOSE_MARKETPLACE_CUSTOM_ASSET, UPLOAD_POLICIES } from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";
import {
  putToSignedUrl,
  stageMemoryPreflightUpload,
  type UploadConfirmResult,
  type UploadPresignResult,
} from "@/features/preflight/api/uploads.api";
import { resolveCustomAssetContentType } from "@/features/marketplace/lib/resolve-custom-asset-content-type";

const CUSTOM_ASSET_POLICY = UPLOAD_POLICIES[UPLOAD_PURPOSE_MARKETPLACE_CUSTOM_ASSET];

async function presignCustomAssetUpload(input: {
  tenantId: string;
  contentType: string;
  sizeBytes: number;
  originalName: string;
}): Promise<UploadPresignResult> {
  const { data } = await authFetch<UploadPresignResult>("/uploads/presign", {
    method: "POST",
    body: JSON.stringify({
      tenantId: input.tenantId,
      purpose: UPLOAD_PURPOSE_MARKETPLACE_CUSTOM_ASSET,
      contentType: input.contentType,
      sizeBytes: input.sizeBytes,
      originalName: input.originalName,
    }),
  });
  return data;
}

async function confirmCustomAssetUpload(input: {
  tenantId: string;
  uploadId: string;
}): Promise<UploadConfirmResult> {
  const { data } = await authFetch<UploadConfirmResult>("/uploads/confirm", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data;
}

export async function runCustomAssetUploadFlow(input: {
  tenantId: string;
  file: File;
}): Promise<UploadConfirmResult> {
  const contentType = resolveCustomAssetContentType(input.file, CUSTOM_ASSET_POLICY);
  const presign = await presignCustomAssetUpload({
    tenantId: input.tenantId,
    contentType,
    sizeBytes: input.file.size,
    originalName: input.file.name,
  });

  if (presign.requiresMemoryStage || presign.requiresServerStage) {
    await stageMemoryPreflightUpload({
      tenantId: input.tenantId,
      uploadId: presign.uploadId,
      contentType,
      file: input.file,
    });
  } else {
    await putToSignedUrl(presign.uploadUrl, input.file, presign.requiredHeaders);
  }

  return confirmCustomAssetUpload({
    tenantId: input.tenantId,
    uploadId: presign.uploadId,
  });
}
